/** A tour PATCH 400 in the record editor's terms (plan §3 `useTourApply`): the view's
 *  nested, field-keyed body flattened onto `"<entity>:<id>:<path>"` change keys, so the
 *  errors land on their fields and ledger rows.
 *
 *  - `{"meeting_point": {"name": "…"}}` → `tour:5:meeting_point.name`;
 *    `{"context": {"notes_link": "…"}}` → `tour:5:context.notes_link`.
 *  - A collection's positional key maps back through the compile `index`:
 *    `{"additional_custom_ids": {"2": "Custom ID is required."}}` → the row sent third →
 *    `alias:<id>:alias.custom_id` (a row-level message lands on the row's
 *    `rowErrorPath`; `{"2": {"integration": "…"}}` on `alias.integration`).
 *  - An error about a row's field this save did not change (a new row's untouched
 *    default) still lands on the ROW, not the page: on a change of that row the save
 *    holds ("External ID: Custom ID is required." on its Matching change), else on the
 *    collection's membership list ("NEW-1: Custom ID is required." — the list that added
 *    the row). Only a row the save holds nothing of becomes a form error.
 *  - `{"pre_trip_items": {"1": "Role #12 is not one of this operator's roles."}}` (the
 *    view's pre-check of the "Visible to" ids) lands on the row the same way, unprefixed
 *    — the message names its subject.
 *  - The itinerary's positional 400s map back through `index.days` / `index.slots` /
 *    `index.activities`: `{"itinerary": {"days": {"2": {"timeslots": {"1": {"time": "…"}}}}}}`
 *    → the stop sent second in the third day → `slot:<id>:slot.time`; a row-level message
 *    (a plain string at a row's position) lands on the row's main field. Not a change of
 *    this save: a change of the same record ("Time: …"), else the parent's list that holds
 *    the row (`day.slots` — "NEW-2: …" / "Day 3, stop 2: …"), else the tour's day list,
 *    else a form error ("Itinerary · Day 3, stop 2: …"). `{"kml_file": "…"}` lands on the
 *    tour-level route file (B6).
 *  - `detail` / `non_field_errors`, and any key that is not a change of this save (an
 *    error about a stored value the draft did not touch), become form-level errors —
 *    named, so the ledger's summary says which field. */
import { changeKey, type DraftWireChange } from '@/features/records/draft/changeSet';
import { isChangeErrorKey, type ValidationFailure } from '@/features/records/editor/errors';
import type { RecordFieldError } from '@/features/records/editor/types';

import {
  activityRec,
  DAY_SLOTS,
  dayRec,
  DAYS_PATH,
  newRowName,
  SLOT_ACTIVITIES,
  slotRec,
  TOUR_KML,
} from '../itinerary/model';

import { TOUR_ENTITY, TOUR_FIELD_BY_KEY } from './catalog';
import { COLLECTIONS, type CollectionKey } from './collections';
import type { TourPatchIndex } from './compile';

const FORM_KEYS = new Set(['detail', 'non_field_errors', 'errors']);

function messages(v: unknown): string[] {
  if (typeof v === 'string') return [v];
  if (Array.isArray(v) && v.every((x) => typeof x === 'string')) return v as string[];
  return [];
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Every leaf message with its path segments. */
function leaves(v: unknown, path: string[] = [], out: Array<{ path: string[]; message: string }> = []) {
  const ms = messages(v);
  if (ms.length > 0) {
    for (const m of ms) out.push({ path, message: m });
  } else if (isObject(v)) {
    for (const [k, child] of Object.entries(v)) leaves(child, [...path, k], out);
  } else if (Array.isArray(v)) {
    v.forEach((child, i) => leaves(child, [...path, String(i)], out));
  }
  return out;
}

const COLLECTION_TITLES: Record<CollectionKey, string> = {
  additional_custom_ids: 'External IDs',
  tag_categories: 'Tag categories',
  pre_trip_items: 'Pre-trip items',
};

/** A wire field of a day / stop / activity row → its draft path; `''` = the row itself. */
const ITINERARY_WIRE: Record<'day' | 'slot' | 'activity', Record<string, string>> = {
  day: { title: 'day.title', subtitle: 'day.subtitle', description: 'day.description', day_span: 'day.day_span', kml_file: 'day.kml' },
  slot: {
    time: 'slot.time',
    title: 'slot.title',
    subtitle: 'slot.subtitle',
    description: 'slot.description',
    timeline_display: 'slot.timeline_display',
    timeline_text: 'slot.timeline_text',
    show_transport: 'slot.show_transport',
    next_transport: 'slot.next_transport',
    transport_type: 'slot.next_transport',
    transport_text: 'slot.transport_text',
  },
  activity: {
    activity: 'activity.activity',
    cost_prefix: 'activity.cost_prefix',
    cost_min: 'activity.cost_min',
    cost_max: 'activity.cost_max',
    cost_unit: 'activity.cost_unit',
    currency: 'activity.currency',
    display_name: 'activity.display_name',
    display_on_map: 'activity.display_on_map',
  },
};
/** Where a message about a whole row lands. */
const ITINERARY_ROW_PATH = { day: 'day.title', slot: 'slot.time', activity: 'activity.activity' } as const;

export function tourValidationFailure(
  body: unknown,
  ctx: { tourId: number; index: TourPatchIndex; sent: readonly DraftWireChange[] },
): ValidationFailure {
  const errors: Record<string, RecordFieldError[]> = {};
  const add = (key: string, message: string) => {
    (errors[key] ??= []).push({ code: 'invalid', message });
  };
  const sentKeys = new Set(ctx.sent.map((c) => changeKey(`${c.entity}:${c.id}`, c.field ?? c.action ?? '')));
  const byKey = new Map<string, (typeof COLLECTIONS)[number]>(COLLECTIONS.map((c) => [c.key, c]));

  const tourRec = `${TOUR_ENTITY}:${ctx.tourId}`;
  /** The first change of this save on `rec` (sorted, for a stable choice). */
  const siblingOf = (rec: string) => [...sentKeys].filter((k) => k.startsWith(`${rec}:`)).sort()[0];

  const fileItinerary = (rest: string[], message: string) => {
    const daysKey = changeKey(tourRec, DAYS_PATH);
    const atList = (label: string | null) => {
      const text = label ? `${label}: ${message}` : message;
      if (sentKeys.has(daysKey)) add(daysKey, text);
      else add('itinerary', `Itinerary${label ? ` · ${label}` : ''}: ${message}`);
    };
    const di = rest[0] === 'days' && rest[1] !== undefined ? Number(rest[1]) : NaN;
    const dayId = Number.isInteger(di) ? ctx.index.days[di] : undefined;
    if (dayId === undefined) {
      atList(null);
      return;
    }
    let entity: 'day' | 'slot' | 'activity' = 'day';
    let rec = dayRec(dayId);
    let id = dayId;
    let parent: string = daysKey;
    let label = `Day ${di + 1}`;
    let i = 2;
    if (rest[i] === 'timeslots' && rest[i + 1] !== undefined) {
      const si = Number(rest[i + 1]);
      const slotId = ctx.index.slots?.[di]?.[si];
      if (slotId !== undefined) {
        entity = 'slot';
        parent = changeKey(rec, DAY_SLOTS);
        rec = slotRec(slotId);
        id = slotId;
        label = `${label}, stop ${si + 1}`;
        i += 2;
        if (rest[i] === 'activities' && rest[i + 1] !== undefined) {
          const ai = Number(rest[i + 1]);
          const activityId = ctx.index.activities?.[di]?.[si]?.[ai];
          if (activityId !== undefined) {
            entity = 'activity';
            parent = changeKey(rec, SLOT_ACTIVITIES);
            rec = activityRec(activityId);
            id = activityId;
            label = `${label}, activity ${ai + 1}`;
            i += 2;
          }
        }
      }
    }
    const field = rest[i];
    const fieldPath = field === undefined ? ITINERARY_ROW_PATH[entity] : ITINERARY_WIRE[entity][field];
    const key = fieldPath ? changeKey(rec, fieldPath) : null;
    if (key && sentKeys.has(key)) {
      add(key, message);
      return;
    }
    const fieldLabel = field === undefined ? null : fieldPath ? (TOUR_FIELD_BY_KEY.get(fieldPath)?.label ?? field) : field;
    const sibling = siblingOf(rec);
    if (sibling) add(sibling, fieldLabel ? `${fieldLabel}: ${message}` : message);
    else if (sentKeys.has(parent)) add(parent, `${id < 0 ? newRowName(id) : label}: ${message}`);
    else atList(label);
  };

  for (const { path, message } of leaves(isObject(body) ? body : {})) {
    const [head, ...rest] = path;
    if (head === 'code') continue;
    if (!head || FORM_KEYS.has(head)) {
      add('detail', message);
      continue;
    }
    if (head === 'itinerary') {
      fileItinerary(rest, message);
      continue;
    }
    if (head === 'kml_file') {
      const key = changeKey(tourRec, TOUR_KML);
      if (sentKeys.has(key)) add(key, message);
      else add('kml_file', `Tour route (KML): ${message}`);
      continue;
    }
    const spec = byKey.get(head);
    if (spec) {
      const [pos, field] = rest;
      const rowId = pos !== undefined ? ctx.index[spec.key][Number(pos)] : undefined;
      if (rowId === undefined) {
        add(head, `${COLLECTION_TITLES[spec.key]}: ${message}`);
        continue;
      }
      const rec = `${spec.entity}:${rowId}`;
      const path = field ? `${spec.entity}.${field}` : spec.rowErrorPath;
      const key = changeKey(rec, path);
      if (sentKeys.has(key)) {
        add(key, message);
        continue;
      }
      // The field was not part of this save: file it on the row all the same.
      const fieldLabel =
        !field && spec.rowErrorLabel === null ? null : (TOUR_FIELD_BY_KEY.get(path)?.label ?? field ?? 'Row');
      const sibling = [...sentKeys].filter((k) => k.startsWith(`${rec}:`)).sort()[0];
      const membership = changeKey(`${TOUR_ENTITY}:${ctx.tourId}`, spec.membership);
      if (sibling) add(sibling, fieldLabel ? `${fieldLabel}: ${message}` : message);
      else if (sentKeys.has(membership)) add(membership, `${rowId < 0 ? `NEW-${-rowId}` : `Row ${Number(pos) + 1}`}: ${message}`);
      else add(head, `${COLLECTION_TITLES[spec.key]}: ${message}`);
      continue;
    }
    const fieldPath = path.join('.');
    const key = changeKey(`${TOUR_ENTITY}:${ctx.tourId}`, fieldPath);
    if (sentKeys.has(key)) add(key, message);
    // A stored value the draft did not touch: say which field.
    else add(fieldPath, `${TOUR_FIELD_BY_KEY.get(fieldPath)?.label ?? fieldPath}: ${message}`);
  }

  const keys = Object.keys(errors);
  const formMessage = keys.filter((k) => !isChangeErrorKey(k)).map((k) => errors[k]![0]!.message)[0];
  return {
    code: 'invalid',
    message:
      keys.length === 0
        ? 'The server refused the changes.'
        : keys.every((k) => !isChangeErrorKey(k)) && formMessage
          ? formMessage
          : 'The server refused some of the changes — see the marked fields.',
    errors,
  };
}
